<template>
  <step-container
    title="¡Estamos casi listos! Guardaremos tus opciones y te enviaremos un mail con los pasos a seguir"
    class="scroll-m-40"
    :number="stepNumber"
  >
    <div class="w-full">
      <div class="mx-auto max-w-md flex flex-col gap-2">
        <div class="flex gap-4 justify-between">
          <ValidationProvider
            name="firstName"
            :rules="{ required: true }"
            v-slot="{ errors }"
            class="flex-auto"
          >
            <text-input-group
              ref="firstNameInput"
              label="Nombre"
              placeholder="Taylor"
              type="text"
              name="firstName"
              v-model="firstName"
              required
            />
            <div class="h-4 text-xs text-red-600">{{ errors[0] }}</div>
          </ValidationProvider>
          <ValidationProvider
            name="lastName"
            :rules="{ required: true }"
            v-slot="{ errors }"
            class="flex-auto"
          >
            <text-input-group
              ref="lastNameInput"
              label="Apellido"
              placeholder="Swift"
              type="text"
              name="lastName"
              v-model="lastName"
              required
            />
            <div class="h-4 text-xs text-red-600">{{ errors[0] }}</div>
          </ValidationProvider>
        </div>

        <div class="mb-4">
          <label class="flex text-sm font-medium leading-6" for="phone-input"
            >Teléfono<div class="text-pink-600">*</div></label
          >
          <vue-phone-number-input
            :id="'phone-input'"
            v-model="phone"
            @update="setPhone"
            default-country-code="CL"
            :preferred-countries="['CL', 'PE', 'UY', 'CO', 'AR', 'EC', 'ES', 'MX']"
            color="#CD0056"
            clearable
            no-example
            required
            size="lg"
            type="tel"
            name="cellphone"
            class="border-0 p-0 shadow-sm font-['Montserrat']"
            :border-radius="6"
            :translations="{
              countrySelectorLabel: 'País',
              countrySelectorError: 'Código país inválido',
              phoneNumberLabel: 'Teléfono',
              example: 'Ejemplo',
            }"
          />
        </div>

        <ValidationProvider
          name="email"
          :rules="{ required: true, email: true }"
          v-slot="{ errors }"
        >
          <text-input-group
            placeholder="mi-correo@ejemplo.com"
            class=""
            type="email"
            name="email"
            label="E-mail"
            v-model="email"
            required
          />
          <div v-if="errors.length" class="h-4 text-xs text-red-600">{{ errors[0] }}</div>
        </ValidationProvider>

        <div v-if="!!takenEmailError" class="text-xs pb-2">{{ takenEmailError }}. <a :href="loginPath" class="text-pink-600 hover:underline">Inicia sesión</a> si ya tienes una cuenta.</div>

        <label class="flex w-full justify-center items-center" for="acceptTerms">
          <input
            type="checkbox"
            id="acceptTerms"
            v-model="acceptTerms"
            class="rounded focus-visible:ring-pink-600 focus:ring-transparent text-pink-600 transition duration-150 ease-in-out"
          />
          <span class="ml-2 text-sm text-gray-500"
            >Acepto los
            <a
              href="/legal/terminos_y_condiciones.pdf"
              target="_blank"
              rel="noopener noreferrer"
              class="text-pink-600 underline"
              >términos y condiciones</a
            ></span
          >
        </label>

        <div class="text-center mt-8">
          <styled-button
            :disabled="!isFormValid || isLoading"
            title="Continuar"
            @click="submitQuotation()"
          />
        </div>
      </div>
    </div>
  </step-container>
</template>

<script>
import stepMixin from "../../mixins/step";
import optionMixin from "../../mixins/option";

import { ValidationProvider } from "vee-validate";
import "../../../../../utils/inputValidationRules";
import TextInputGroup from "../TextInputGroup.vue";
import StyledButton from "../layouts/StyledButton.vue";
import { createQuotation } from "../../../../../api/quotation";

export default {
  mixins: [stepMixin, optionMixin],
  inject: ["isPriceHidden", "loginPath"],
  components: { TextInputGroup, ValidationProvider, StyledButton },

  data() {
    return {
      firstName: this.value.contactFirstName,
      lastName: this.value.contactLastName,
      phone: this.value.contactCellphone,
      email: this.value.contactEmail,
      acceptTerms: true,
      scrollTimeout: null,
      isLoading: false,
      takenEmailError: null,
    };
  },
  watch: {
    firstName(value) {
      this.$emit("input", { ...this.value, contactFirstName: value });
    },
    lastName(value) {
      this.$emit("input", { ...this.value, contactLastName: value });
    },

    email(value) {
      this.$emit("input", { ...this.value, contactEmail: value });
      this.takenEmailError = null;
    },
  },
  computed: {
    isFormValid() {
      return (
        this.firstName &&
        this.lastName &&
        this.phone &&
        this.email &&
        this.acceptTerms &&
        this.validateEmail(this.email) &&
        !this.takenEmailError
      );
    },
  },
  methods: {
    validateEmail(email) {
      const regex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;

      return regex.test(email);
    },

    setPhone(updateData) {
      const { formattedNumber, isValid } = updateData;

      this.$emit("input", {
        ...this.value,
        contactCellphone: isValid ? formattedNumber : null,
      });
    },
    async submitQuotation() {
      const quotationData = {
        motive: this.value.motive,
        interview_service_id: this.value.interviewServiceId,
        format: this.value.format,
        modality: this.value.modality,
        protagonist_first_name: this.value.protagonistFirstName,
        protagonist_last_name: this.value.protagonistLastName,
        protagonist_nickname: this.value.protagonistNickname ?? null,
        protagonist_interviewee: this.value.protagonistInterviewee,
        additional_info: this.value.additionalInfo,
        contact_first_name: this.value.contactFirstName,
        contact_last_name: this.value.contactLastName,
        contact_cellphone: this.value.contactCellphone,
        email: this.value.contactEmail,
      };

      this.isLoading = true;
      try {
        const response = await createQuotation({ quotation: quotationData });
        this.$emit('track-quotation', this.value);
        window.location.href = `/quotations/${response.data.token}/success`;
      } catch (error) {
        if (error.response.status === 422) {
          this.takenEmailError = "Este correo ya se encuentra en uso";
        }
        console.warn(error);
      }
      this.isLoading = false;
    },
  },
};
</script>
